import React, { memo, useCallback, useState, useRef } from 'react';
import styled from 'styled-components';
import { Theme } from '../styles/theme';
import { useDispatch, useSelector } from 'react-redux';
import { SearchItem } from '../cards/search/search.slice';
import { 
  toggleCardSelection, 
  selectAllCards,
  deselectAllCards,
  selectMemorizeState 
} from './memorize.slice';
import { cardsActions } from '../cards/cards.slice';
import { CardState } from '../common';
import { useConfirmDialog } from '../components/ConfirmDialogContext';

interface Props {
  cards: SearchItem[];
}

export const MemorizeCardList = memo(({ cards }: Props) => {
  const dispatch = useDispatch();
  const { selectedCardIds } = useSelector(selectMemorizeState);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLButtonElement>(null);
  
  const { openConfirmDialog } = useConfirmDialog();

  const handleToggleCard = (cardId: string) => {
    dispatch(toggleCardSelection(cardId));
  };
  
  const handleSelectAll = () => {
    dispatch(selectAllCards());
  };
  
  const handleDeselectAll = () => {
    dispatch(deselectAllCards());
  };

  const closeActionsMenu = useCallback(() => {
    setIsActionsMenuOpen(false);
  }, []);

  const toggleActionsMenu = useCallback(() => {
    setIsActionsMenuOpen(prev => !prev);
  }, []);
  
  const handleDropdownClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);
  
  const handleMoveToKnownClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    openConfirmDialog(
      'Move to Known',
      `Are you sure you want to move ${selectedCardIds.length} selected cards to "Known" state?`,
      () => {
        selectedCardIds.forEach(id => {
          dispatch(cardsActions.moveToState({ cardId: id, state: CardState.Known }));
        });
        dispatch(deselectAllCards());
        closeActionsMenu();
      }
    );
  }, [selectedCardIds, dispatch, openConfirmDialog, closeActionsMenu]);
  
  const handleMoveToFutureClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    openConfirmDialog(
      'Move to Future',
      `Are you sure you want to move ${selectedCardIds.length} selected cards to "Future" state?`,
      () => {
        selectedCardIds.forEach(id => {
          dispatch(cardsActions.moveToState({ cardId: id, state: CardState.Future }));
        });
        dispatch(deselectAllCards());
        closeActionsMenu();
      }
    );
  }, [selectedCardIds, dispatch, openConfirmDialog, closeActionsMenu]);

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    openConfirmDialog(
      'Delete Cards',
      `Are you sure you want to delete ${selectedCardIds.length} selected cards? This action cannot be undone.`,
      () => {
        selectedCardIds.forEach(id => {
          dispatch(cardsActions.deleteCard(id));
        });
        dispatch(deselectAllCards());
        closeActionsMenu();
      }
    );
  }, [selectedCardIds, dispatch, openConfirmDialog, closeActionsMenu]);
  
  const isAllSelected = cards.length > 0 && selectedCardIds.length === cards.length;
  const isPartiallySelected = selectedCardIds.length > 0 && selectedCardIds.length < cards.length;
  
  return (
    <Container $hasSelectedCards={selectedCardIds.length > 0}>
      <Header>
        <SelectAllContainer>
          <Checkbox
            type="checkbox"
            checked={isAllSelected}
            ref={input => {
              if (input) {
                input.indeterminate = isPartiallySelected;
              }
            }}
            onChange={isAllSelected ? handleDeselectAll : handleSelectAll}
          />
          <SelectAllLabel>
            {selectedCardIds.length} of {cards.length} selected
          </SelectAllLabel>
        </SelectAllContainer>
      </Header>
      
      <CardsList>
        {cards.map(card => (
          <CardItem key={card.id}>
            <Checkbox
              type="checkbox"
              checked={selectedCardIds.includes(card.id)}
              onChange={() => handleToggleCard(card.id)}
            />
            <CardQuestion>
              {card.title || 'No question found'}
            </CardQuestion>
          </CardItem>
        ))}
      </CardsList>
      
      {selectedCardIds.length > 0 && (
        <>
          {isActionsMenuOpen && <Overlay onClick={closeActionsMenu} />}
          <GroupActionsPanel>
            <SelectedCount>
              {selectedCardIds.length} selected
            </SelectedCount>
            
            <ActionsContainer>
              <ActionButton onClick={handleSelectAll}>
                Select All
              </ActionButton>
              
              <ActionButton onClick={handleDeselectAll}>
                Clear
              </ActionButton>
              
              <ActionsMenuButton 
                ref={actionsMenuRef}
                onClick={toggleActionsMenu}
              >
                Actions
              </ActionsMenuButton>
              
              {isActionsMenuOpen && (
                <ActionsDropdown onClick={handleDropdownClick}>
                  <ActionOption onClick={handleMoveToKnownClick}>
                    Move to Known
                  </ActionOption>
                  <ActionOption onClick={handleMoveToFutureClick}>
                    Move to Future
                  </ActionOption>
                  <ActionOption className="delete" onClick={handleDeleteClick}>
                    Delete
                  </ActionOption>
                </ActionsDropdown>
              )}
            </ActionsContainer>
          </GroupActionsPanel>
        </>
      )}
    </Container>
  );
});

const Container = styled.div<{ $hasSelectedCards?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${(props: { theme: Theme }) => props.theme.primaryBackground};
  border-right: 1px solid ${(props: { theme: Theme }) => props.theme.primaryBorder};
  position: relative;
`;

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${(props: { theme: Theme }) => props.theme.primaryBorder};
  background: ${(props: { theme: Theme }) => props.theme.secondaryBackground};
`;

const SelectAllContainer = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const SelectAllLabel = styled.span`
  margin-left: 8px;
  font-size: 14px;
  color: ${(props: { theme: Theme }) => props.theme.secondaryText};
`;

const CardsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`;

const CardItem = styled.label`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: ${(props: { theme: Theme }) => props.theme.hoverBackground};
  }
`;

const Checkbox = styled.input`
  margin-right: 12px;
  cursor: pointer;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`;

const CardQuestion = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: ${(props: { theme: Theme }) => props.theme.primaryText};
`;

const GroupActionsPanel = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${(props: { theme: Theme }) => props.theme.secondaryBackground};
  border-top: 1px solid ${(props: { theme: Theme }) => props.theme.primaryBorder};
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SelectedCount = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${(props: { theme: Theme }) => props.theme.primaryText};
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  position: relative;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${(props: { theme: Theme }) => props.theme.neutralButtonBorder};
  border-radius: 4px;
  background: ${(props: { theme: Theme }) => props.theme.neutralButtonBackground};
  color: ${(props: { theme: Theme }) => props.theme.neutralButtonForeground};
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: ${(props: { theme: Theme }) => props.theme.neutralButtonBorder};
  }
`;

const ActionsMenuButton = styled(ActionButton)`
  background: ${(props: { theme: Theme }) => props.theme.primaryColor || '#007bff'};
  color: white;
  border-color: ${(props: { theme: Theme }) => props.theme.primaryColor || '#007bff'};
  
  &:hover {
    background: ${(props: { theme: Theme }) => props.theme.primaryColor || '#007bff'};
    opacity: 0.9;
  }
`;

const ActionsDropdown = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 4px;
  background: ${(props: { theme: Theme }) => props.theme.secondaryBackground};
  border: 1px solid ${(props: { theme: Theme }) => props.theme.primaryBorder};
  border-radius: 6px;
  min-width: 140px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 101;
  overflow: hidden;
`;

const ActionOption = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  color: ${(props: { theme: Theme }) => props.theme.primaryText};
  transition: background-color 0.2s ease;
  font-size: 13px;
  
  &:hover {
    background: ${(props: { theme: Theme }) => props.theme.hoverBackground};
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid ${(props: { theme: Theme }) => props.theme.primaryBorder};
  }
  
  &.delete {
    color: ${(props: { theme: Theme }) => props.theme.dangerColor || '#dc3545'};
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: 99;
`;